import * as Sentry from '@sentry/react';
import _cloneDeep from 'lodash/cloneDeep';
import { produce } from 'immer';
import { createSelector } from 'reselect';
import { current, isDraft } from 'immer';
import { STRUCTURAL_TYPES, } from 'src/app/slicedForm/mapping/mappingDirections/index';
import { getMarketStartString } from '../../utility/filterTimeSliceFunctions';
import {
  BOOLEAN_OPS,
  DATE_TYPES,
  ROLLING_DATE_OPS,
  VALUE_TYPES,
  defaultEntity,
} from 'src/app/slicedForm/FilterForm/definitions/inputEnums';
import {
  getActiveProfile,
  selectActiveProfile,
  COPY_PROFILE,
  ADD_PROFILE
} from 'src/app/slicedForm/shared/reducers/profileReducer';
import {
  addLeaf,
  createEntityAndLeaf,
  deleteLeaf,
  getParentLeaf,
  getAllDescendants,
  getLeaf,
  copyEntity,
  replaceLeaf,
  getSliceChildrenLeaves,
  createEntityValues,
  createEntityVal,
  getEntityVal,
  createEntityId,
} from 'src/app/slicedForm/mapping/formTreeManipulation';

/**
 * @typedef {import('src/app/slicedForm/utility/filterTimeSliceFunctions.js').Slice} Slice
 */


/**
 * NEXT REFACTOR, WHAT THE FUCk:
 * 
 * 1) profiles and filters are intertwined. We can't separate them
 * 2) FormActions is useless
 * 3) Errors need thier own position in state. Save entity errors to a global key
 * 4) React hook form:
 * 
 * 
 */



/**
 * State revolves around two structures. 
 *  1) A flat map of entities to attributes
 *  2) A recursive tree structure of leaf nodes
 * 
 * Leafs refer to entities to get thier data. The tree specifies rendering order and location.
 * 
 * Lots of these updates are very manual, and somewhat painful. I'm not sure if we've chosen the wrong structure,
 * or if thats how tree manipulation feels when you can't use object references like a normal tree. All updates 
 * must operate on serializable data, effecitvely we're manipulating JSON.
 * 
 * The hard part is that these GROUPS feel simple, but hide a huge amount of structural complexity. SLICE_GROUPS and OR groups and AND groups
 * all have different rules. Creating a GROUP might actually have 3 or 4 context-dependant rules, that aren't obvious to a programmer.
 * 
 * example: 
 *  Click the OR button. What happens?
 *    - Is the parent an AND? 
 *        1) Replace the clicked FILTER with a new OR
 *        2) Add the clicked FILTER as a child of the new OR
 *        3) Add a new empty FILTER as the second child of the OR
 *    - Is the parent an OR?
 *        1) Add a new empty FILTER as next child of OR
 *    - Is the parent a SLICE?
 *       - Is the grandparent an AND?
 *          1) Replace the entire SLICE_GROUP node with an OR
 *          2) Add the SLICE_GROUP as the first child of the OR
 *          3) Add a new empty FILTER as the second child of the OR, but not the SLICE
 *       - Is the grandparent an OR?
 *          1) Create a blank filter in the OR, but not the SLICE
 *          
 * 
 * One potential update would be to store things in a truly flat structure. Just entities, with child_ids (and maybe parent_id).
 * I'm not sure this would make updating easier, tree manipulation isn't really the hard part. It would be more
 * performant however, since modifying tree structure wouldn't cause a full re-render of the form.
 * 
 * As it stands, these structural re-renders aren't that bad. Updating filter values is fast, and thats what matters.
 */


/** For now, the lefthand binary arg is always type: 'column'. Slice group children must be syncronized. */
export const UPDATE_FILTER_LEFT_COLUMN = '@form-filter/UPDATE_FILTER_LEFT_COLUMN';
/** Various flags don't fit well into a semantic query. filter.left object can include extra properties for these flags. Like 'allowNull' */
export const UPDATE_FILTER_LEFT_EXTRA_OPTIONS = '@form-filter/UPDATE_FILTER_LEFT_EXTRA_OPTIONS';
/** Updates the { value, column } properties only */
export const UPDATE_FILTER_RIGHT_VALUE = '@form-filter/UPDATE_FILTER_RIGHT_VALUE';
/** Updates the { type } property to either 'column' or 'filter'. Separate from VALUE because slice children need to inherit. */
export const UPDATE_FILTER_RIGHT_VALUE_TYPE = '@form-filter/UPDATE_FILTER_RIGHT_TYPE';
/** Rolling dates suck. We can't use the regular structure for them. We need a selector between rolling and regular, located in filter.right */
export const UPDATE_FILTER_DATE_TYPE = '@form-filter/UPDATE_FILTER_DATE_TYPE';
/** Updates the <=, >= arguments. Slice group children must be syncronized. */
export const UPDATE_FILTER_OPERATOR = '@form-filter/UPDATE_FILTER_OPERATOR';
/** Take the given FILTER, and wrap it within a new SLICE_GROUP */
export const CREATE_SLICE_GROUP = '@form-filter/CREATE_SLICE_GROUP';
/** Add another child to an existing SLICE_GROUP */
export const CREATE_SLICE = '@form-filter/CREATE_SLICE';
/** Take the given FILTER, and wrap within a new OR group */
export const CREATE_OR_GROUP = '@form-filter/CREATE_OR_GROUP';
/** Modify a single SLICE child with a new time */
export const UPDATE_SLICE_TIME = '@form-filter/UPDATE_SLICE_TIME';
/** Add a new filter to the parent group. Can be AND or OR. */
export const CREATE_FILTER = '@form-filter/CREATE_FILTER';
/** Delete an entity and its descendants, from both the tree and the entities. */
export const DELETE_ENTITY = '@form-filter/DELETE_ENTITY';
/** Reorders leaves within a given groupId's tree */
export const REORDER_LEAF = '@form-filter/REORDER_LEAF';

export const REGISTER_REF = '@form-filter/REGISTER_REF';
export const UPDATE_ENTITY_VALUE = '@form-filter/UPDATE_ENTITY_VALUE';


window.printprox = (el) => {
  console.log(isDraft(el) ? current(el) : el);
};


const exists = (val) => {
  return val !== undefined && val !== null;
}


const validateEntity = (entities, id) => {

  const getVal = (path) => {
    return getEntityVal(entities[id]?.[path]);
  }


  const errors = {};
  const entity = entities[id];

  if (!entity) {
    return;
  }

  // left
  if (getVal('left.type') === VALUE_TYPES.value && !exists(getVal('left.value'))) {
    errors['left.column'] = 'Value required'
  }
  else if (getVal('left.type') === VALUE_TYPES.column && !getVal('left.column')) {
    errors['left.column'] = 'Column required';
  }

  // right
  let rights = getVal('operator') === BOOLEAN_OPS.BTW
    ? ['0', '1']
    : ['0'];

  for (const idx of rights) {
    if (getVal(`right[${idx}].type`) === VALUE_TYPES.value && !exists(getVal(`right[${idx}].value`))) {
      errors[`right[${idx}].value`] = 'Value required';
    } else if (getVal(`right[${idx}].type`) === VALUE_TYPES.value && Array.isArray(getVal(`right[${idx}].value`)) && !getVal(`right[${idx}].value`).length) {
      errors[`right[${idx}].value`] = 'Value required';
    } else if (getVal(`right[${idx}].type`) === VALUE_TYPES.column && !getVal(`right[${idx}].column`)) {
      errors[`right[${idx}].column`] = 'Column required';
    }
  }

  // slice
  if ('startTime' in entity && !getVal('startTime')) {
    errors['startTime'] = 'Time required';
  }

  Object.keys(entity).forEach(path => {
    if (!(path in errors) && entity[path]?.err) {
      entity[path].err = null;
    }
  });
  Object.keys(errors).forEach(errPath => {
    if (entity[errPath]) {
      entity[errPath].err = errors[errPath];
    } else {
      entity[errPath] = { err: errors[errPath] };
    }
  })
}



/**
 * When profiles are copied, we need to also copy all entities/nodes.
 * Then we need to replace all the old entitiy IDs with the copied ones.
 * @param {Object} entities (immer object)
 * @param {Object} root (immer object)
 */
function doCopyAndReferenceEntities(entities, root, removeErrors = true) {
  const recurse = (node) => {
    if (node?.id) {
      let newId;
      if (entities?.[node.id]) {
        // copy an entity for FILTER nodes
        const { id, entity } = copyEntity(entities[node.id], removeErrors);
        newId = id;
        entities[newId] = entity;
        validateEntity(entities, newId);
      } else {
        // generate an ID for STRUCTURAL nodes
        newId = createEntityId();
      }
      node.id = newId;
    }
    if (node?.tree) {
      node.tree.forEach(recurse);
    }
  }

  recurse(root);
}


/**
 * Add a blank SLICE filter to an existing group. Called after CREATE_SLICE_GROUP
 * to give the user another input, and during CREATE_SLICE as the main behavior.
 * @param {Object} draft
 * @param {Object} action
 * @returns {boolean} - success or failure
 */
function doCreateEmptySlice(draft, action) {
  // Add a new blank node to the given SLICE_GROUP.
  const { groupId, data = {} } = action.payload;

  const profile = getActiveProfile(draft);
  if (!profile) return false;

  const sliceGroupLeaf = getLeaf(profile.root, groupId);
  if (!sliceGroupLeaf) {
    console.warn('No slice group leaf for id', groupId);
    return false;
  }


  const prevSliceLeaf = sliceGroupLeaf.tree?.[sliceGroupLeaf.tree.length - 1];

  const prevSliceEntity = draft.entities?.[prevSliceLeaf?.id];
  if (!prevSliceEntity) {
    console.warn(`No first slice node found for group ${groupId}`);
    return false;
  }

  // copy first slice settings
  const newEntityValues = _cloneDeep(prevSliceEntity);
  newEntityValues.startTime = createEntityVal(null);


  const { id: newFilterId, entity: newFilterEntity, leaf: newFilterLeaf } = createEntityAndLeaf({
    type: STRUCTURAL_TYPES.FILTER,
    values: newEntityValues
  });

  const success = addLeaf(
    profile.root,
    groupId,
    newFilterLeaf,
  );

  if (success) {
    draft.entities[newFilterId] = newFilterEntity;
    validateEntity(draft.entities, newFilterId);
  } else {
    console.warn('Failed to add leaf', newFilterId, groupId);
  }

  return success;
}

/**
 * Create a new OR_GROUP
 * @param {Object} profile - the draft active profile
 * @param {Object[]} entities - the draft entities
 * @param {Leaf} filterLeaf - the leaf of the filter to be grouped
 * @returns {(Leaf|undefined)} - The new OR group leaf (if created)
 */
function doCreateOrGroup(
  profile,
  entities,
  filterLeaf
) {
  // Place the given ID node into a new OR group as the first element.
  const { id: groupId, entity: groupEntity, leaf: groupLeaf } = createEntityAndLeaf({
    type: STRUCTURAL_TYPES.OR,
    tree: [filterLeaf]
  });

  const success = replaceLeaf(
    profile.root,
    filterLeaf.id,
    groupLeaf
  );

  if (!success) {
    console.warn('Failed to replace leaf', filterLeaf.id, groupId);
  }

  return groupLeaf;
}


function doSwapFromSliceToRegular(id, parentLeaf, profile, entities) {
  // We are updating to a column that doesn't allow slices, but we're in a slice group.
  const childToKeep = parentLeaf.tree[0]; // same entity as action.payload.id
  const childrenToDelete = parentLeaf.tree.slice(1);

  // slice group children never have any descendants, but that might change in the future
  let allDescendantsToDelete = []
  for (const child of childrenToDelete) {
    allDescendantsToDelete.push(child, ...getAllDescendants(child));
  }

  // We don't need deleteLeaf(), because we already know all leaves live inside parent slice group.
  // Replace the slice group with the first filter.
  const success = replaceLeaf(
    profile.root,
    parentLeaf.id,
    childToKeep
  );

  if (success) {
    // Delete all the children
    for (const child of allDescendantsToDelete) {
      delete entities[child.id];
    }
    delete entities[childToKeep.id]['startTime']
    validateEntity(entities, childToKeep.id)
  } else {
    Sentry.captureException(`UPDATE_FILTER_LEFT_COLUMN, Failed to replace leaf ${parentLeaf.id} with ${childToKeep.id}. Slice group deletion failed.`);
  }
}


const isValidNumer = (val) => typeof val === 'number';

/**
 * If right[0].value > right[1].value, swap the values.
 * This might not belong here. Validation feels like a UI decision. But it would mean
 * creating a new BETWEEN component that holds and dispatches the values temporarily. Not sure its 
 * worth the effort.
 * @param {Object} entities 
 * @param {string} id 
 * @returns  {bool} true if swapped
 */
const swapBetweenValuesIfNeeded = (entities, id) => {
  const entity = entities?.[id];
  if (!entity) return false;
  if (!getEntityVal(entity?.['operator']) === BOOLEAN_OPS.BTW) return;
  if (!isValidNumer(getEntityVal(entity?.['right[0].value'])) || !isValidNumer(getEntityVal(entity?.['right[1].value']))) return false;

  if (entity['right[0].value'].val <= entity['right[1].value'].val) return false;

  const larger = entity['right[0].value'].val;
  const smaller = entity['right[1].value'].val;
  entities[id]['right[0].value'].val = smaller;
  entities[id]['right[1].value'].val = larger;
}


/**
 * Create an empty FILTER, and add it to the given groupId as a child.
 * @param {Object} profile - the draft active profile
 * @param {Object[]} entities - the draft entities
 * @param {string} groupId - the leaf of the filter to be grouped
 * @param {Object} [data=defalutEntity] - arbitrary node data for the new filter. Nested, not flat.
 * @returns {(Leaf|undefined)} - The new filter leaf (if created)
 */
function doCreateFilter(
  profile,
  entities,
  groupId,
  data = defaultEntity
) {
  if (groupId === 'ROOT') {
    // Performance enhancement, allows components to avoid selecting groupId
    groupId = profile.root.id;
  }

  const { id: newId, entity: newEntity, leaf: newLeaf } = createEntityAndLeaf({
    type: STRUCTURAL_TYPES.FILTER,
    values: { ...createEntityValues(data) }
  });

  const success = addLeaf(
    profile.root,
    groupId,
    newLeaf
  );

  if (success) {
    entities[newId] = newEntity;
    validateEntity(entities, newId);
    return newLeaf;
  }
}

function filterReducer(draft, action) {
  switch (action.type) {

    /**
     * DUPLICATE ACTION. SEE profileReducer.js
     * We need to copy entities, otherwise two profiles can modify eachother.
     */
    case ADD_PROFILE: {
      const { base, id, newId } = action.payload;

      if (!newId) {
        console.warn(new Error('filterReducer.ADD_PROFILE requires newId'));
        return draft;
      }

      const newProfile = draft.profiles.find(p => p.id === newId);
      if (!newProfile) {
        console.warn('filterReducer.ADD_PROFILE could not find newId profile');
        return draft;
      }

      doCopyAndReferenceEntities(draft.entities, newProfile.root, false);

      break;
    }

    /**
     * DUPLICATE ACTION. SEE profileReducer.js
     * We need to copy entities, otherwise two profiles can modify eachother.
     */
    case COPY_PROFILE: {
      const { id, name, newId } = action.payload;

      if (!newId) {
        console.warn(new Error('filterReducer.COPY_PROFILE requires newId'));
        return draft;
      }

      const newProfile = draft.profiles.find(p => p.id === newId);
      if (!newProfile) {
        console.warn('filterReducer.COPY_PROFILE could not find newId profile');
        return draft;
      }

      doCopyAndReferenceEntities(draft.entities, newProfile.root, false);

      break;
    }

    /**
     * Validation rules:
     * If visible and null, invalid
     * If BTW and >, invalid
     */


    /*
     This is absolutely miserable.
     I can't believe how much I hate this.

     The only alternative is to batch multiple updates inside a useEntity.setValues([{ id, path, value, options }, ...])
     This moves all complexity to the component instead.
     Problems are:
       validation and ref tracking becomes rediculous. 
       Can't traverse the tree from the component.
       Can't easily deal with tree modifications/slices. Whats the parent? Whats the order?
         Something like, every component would need a reference to all its slice siblings at all times. 
         Seems real bad.
    */
    case UPDATE_ENTITY_VALUE: {
      const {
        id,
        path,
        value,
        colDef,
        columnDefaults,
        removeFalsy = false,
        defaultDateValue,
      } = action.payload;

      const profile = getActiveProfile(draft);
      const parentLeaf = getParentLeaf(profile.root, id);
      const isSliceGroup = parentLeaf.type === STRUCTURAL_TYPES.SLICE_GROUP;

      let idsToUpdate = [id];
      let pathValues = { [path]: { val: value, removeFalsy } }

      const pathsUniqueToSlice = [
        'right[0].value',
        'right[0].column',
        'right[1].value',
        'right[1].column',
        'startTime'
      ]

      // Conditional modifications to the tree
      if (isSliceGroup) {
        if (path === 'left.column' && colDef && !colDef?.timeSlice) {
          // Delete slice group and children. Lift first child up
          doSwapFromSliceToRegular(id, parentLeaf, profile, draft.entities)
        } else {
          // New column has slices. must propagate value to slice children.
          if (!pathsUniqueToSlice.includes(path)) {
            idsToUpdate = parentLeaf.tree.map(leaf => leaf.id);
          }
        }
      }

      if (path === 'left.column') {
        // The caller wants to reset the right column to the default.
        if (columnDefaults && Object.keys(columnDefaults)) {
          const newPaths = createEntityValues(columnDefaults);
          Object.entries(newPaths).forEach(([path, value]) => {
            pathValues[path] = value;
          })
        } else {
          // Remove right.column for safety, we don't know if the comparison is valid without a lot
          // of work
          pathValues['right[0].column'] = { val: null };
          pathValues['right[1].column'] = { val: null };
        }
      }

      if (path === 'right[0].type') {
        pathValues['right[1].type'] = { val: value }
      }

      if (path === 'operator' && value === BOOLEAN_OPS.BTW && getEntityVal(draft.entities[id]?.operator) !== BOOLEAN_OPS.BTW) {
        pathValues['right[1].type'] = { val: getEntityVal(draft.entities[id]?.['right[0].type']) }
      }

      if (path === 'dateType') {
        const currentOperator = draft.entities[id]?.['operator'];

        // enforce that the operator is valid, according to DATE_TYPE
        const valid_rolling_ops = Object.keys(ROLLING_DATE_OPS);
        const valid_date_ops = Object.keys(BOOLEAN_OPS);

        if (value === DATE_TYPES.ROLLING && !valid_rolling_ops.includes(currentOperator)) {
          pathValues['operator'] = { val: Object.keys(ROLLING_DATE_OPS)[0] };
        }
        else if (value === DATE_TYPES.DATE && !valid_date_ops.includes(currentOperator)) {
          pathValues['operator'] = { val: BOOLEAN_OPS.EQ };
        }
        // Set right value to whatever default the component wants
        pathValues['right[0].value'] = { val: defaultDateValue };
      }

      // Actual update statement
      idsToUpdate.forEach(_id => {
        Object.entries(pathValues).forEach(([path, { val, removeFalsy }]) => {
          if (!draft.entities[_id]?.[path]) {
            draft.entities[_id][path] = { val: null };
          }
          if (!val && removeFalsy) {
            delete draft.entities[_id][path];
          } else if (val !== undefined) {
            draft.entities[_id][path].val = val;
          }
        });

        swapBetweenValuesIfNeeded(draft.entities, _id);
        validateEntity(draft.entities, _id);
      })

      break;
    }


    case CREATE_SLICE_GROUP: {
      // Place the given ID node into a new SLICE_GROUP as the first element.
      // Add a default startTime of 4am to the first child. Maybe theres a more hardened 
      // way of doing the startTime intialization...
      const filterId = action.payload;

      const profile = getActiveProfile(draft);
      if (!profile) break;

      const filterLeaf = getLeaf(profile.root, filterId);
      const filterNode = draft.entities?.[filterId];

      if (!filterLeaf || !filterNode) {
        console.warn(`No filter leaf/node found for id ${filterId}`);
        break;
      }

      const { id: groupId, leaf: groupLeaf } = createEntityAndLeaf({
        type: STRUCTURAL_TYPES.SLICE_GROUP,
        tree: [filterLeaf],
      });

      const success = replaceLeaf(
        profile.root,
        filterId,
        groupLeaf
      );

      if (success) {
        draft.entities[filterId].startTime = createEntityVal(getMarketStartString())
        validateEntity(draft.entities, filterId)
        doCreateEmptySlice(draft, { payload: { groupId } });
      } else {
        console.error('Failed to replace leaf', filterId, groupId);
      }

      break;
    }


    case CREATE_OR_GROUP: {
      const {
        id,
        data
      } = action.payload;
      const profile = getActiveProfile(draft);

      if (!profile) {
        console.warn(`No active profile found for leaf ${id}`);
        break;
      }

      const filterLeaf = getLeaf(profile.root, id);

      if (!filterLeaf) {
        console.warn(`No filter leaf found for id ${id}`);
        break;
      }

      const newGroupLeaf = doCreateOrGroup(profile, draft.entities, filterLeaf);

      if (newGroupLeaf) {
        doCreateFilter(profile, draft.entities, newGroupLeaf.id, data);
      }
      break;
    }


    case CREATE_SLICE: {
      doCreateEmptySlice(draft, action);
      break;
    }


    case DELETE_ENTITY: {
      const { id, groupId } = action.payload;

      const profile = getActiveProfile(draft);
      if (!profile) {
        console.warn(`No active profile found for leaf ${id}`);
        break;
      }

      const parentGroup = getLeaf(profile.root, groupId);

      // If the entity is the first child of a SLICE_GROUP,
      // we need to delete the entire group. This is a UX decision
      if (parentGroup && parentGroup?.type === STRUCTURAL_TYPES.SLICE_GROUP && parentGroup?.tree?.length) {
        const firstChild = parentGroup.tree[0];

        if (firstChild.id === id) {
          // delete group, and all children (including the current ID)
          const deletedGroup = deleteLeaf(profile.root, groupId);

          delete draft.entities[groupId];
          for (const childNode of getAllDescendants(deletedGroup)) {
            delete draft.entities[childNode.id];
          }
          break;
        }
        // Else, continue with the usual logic
      }

      // delete leaf
      const deletedLeaf = deleteLeaf(profile.root, id);
      if (!deletedLeaf) {
        console.warn(`Could not delete leaf ${id}`);
        break;
      }

      delete draft.entities[id];

      // delete child entities
      for (const childNode of getAllDescendants(deletedLeaf)) {
        delete draft.entities[childNode.id];
      }

      if (!parentGroup && !groupId) {
        // AND parent, we're done
        break;
      }


      if (parentGroup?.tree?.length === 0) {
        // If there's no elements in the group, delete it.
        deleteLeaf(profile.root, groupId);
        delete draft.entities[groupId];
      } else if (parentGroup?.tree?.length === 1) {
        /*
          If there's only 1 element left in an SLICE/OR group,
          delete the group and move the element up. A single element is invalid for
          those two group types.
        */
        if ([STRUCTURAL_TYPES.SLICE_GROUP, STRUCTURAL_TYPES.OR].includes(parentGroup?.type)) {
          const childToKeep = parentGroup.tree[0];

          replaceLeaf(
            profile.root,
            groupId,
            childToKeep
          );

          // We're exiting the slice_group, so remove time information
          if (childToKeep.type === STRUCTURAL_TYPES.FILTER) {
            if (draft.entities?.[childToKeep?.id]?.['startTime']) {
              delete draft.entities[childToKeep.id]['startTime'];
            }
            validateEntity(draft.entities, childToKeep.id)
          }
        }
      }
      break;
    }

    case CREATE_FILTER: {
      const { groupId, data } = action.payload;

      const profile = getActiveProfile(draft);
      if (!profile) {
        console.warn(`No active profile found for leaf ${groupId}`);
        break;
      }

      doCreateFilter(profile, draft.entities, groupId, data);

      break;
    }


    case REORDER_LEAF: {
      const { groupId, sourceIndex, destIndex } = action.payload;

      const profile = getActiveProfile(draft);

      if (!profile) {
        console.warn(`No active profile found for leaf ${groupId}`);
        break;
      }

      let id = groupId;
      if (groupId === 'ROOT') {
        id = profile?.root?.id;
      }

      if (!id) {
        console.warn(`No id found for leaf ${groupId}`);
        break;
      }

      const groupLeaf = getLeaf(profile.root, id);

      if (!groupLeaf) {
        console.warn(`No group leaf found for id ${groupId} (${id})`);
        break;
      }

      if (!groupLeaf?.tree || !groupLeaf?.tree?.length) {
        console.warn(`Leaf ${groupId}.tree is empty or missing. Ignoring.`);
        break;
      }

      if (!(sourceIndex in groupLeaf.tree) || !(destIndex in groupLeaf.tree)) {
        console.warn(`Reorder index old:${sourceIndex} new:${destIndex} out of bounds in tree length:${groupLeaf.tree.length}. Ignoring.`);
        break;
      }

      const leafToMove = groupLeaf.tree.splice(sourceIndex, 1)[0];
      groupLeaf.tree.splice(destIndex, 0, leafToMove);

      break;
    }


    default: {
      break;
    }
  }
}

export default filterReducer;



export const selectIsAnyFieldInvalid = createSelector(
  [
    selectActiveProfile,
    state => state.entities
  ],
  (activeProfile, entities) => {
    // column form...
    if (!entities) return false;
    // filter form
    const leaves = getAllDescendants(activeProfile.root);
    return leaves.some(l => {
      const entity = entities[l.id];
      if (!entity) return false;

      return Object.values(entity).some(vals => vals?.err);
    })
  }
)


/**
 
 * Get the SLICE_GROUPs children, used to work with startTimes.
 * 
 * @example
 * const sliceEntities = useParameterizedFormSelector(makeSelectSliceChildEntities, groupId);
 * 
 * @returns {Slice[]} Child entites, or undefined if not a SLICE_GROUP
 */
export const makeSelectSliceChildEntities = () => createSelector(
  [
    selectActiveProfile,
    state => state.entities,
    (_, groupId) => groupId
  ],
  (activeProfile, entities, groupId) => {
    if (!activeProfile) {
      console.error(new Error('No active profile!'))
    }

    const childLeaves = getSliceChildrenLeaves(activeProfile.root, groupId);

    if (childLeaves === undefined) {
      return undefined;
    }

    const childEntites = childLeaves.map(leaf => {
      const entity = entities?.[leaf.id];
      if (!entity) return null;
      return {
        id: leaf?.id,
        startTime: getEntityVal(entity?.['startTime'])
      };
    }).filter(Boolean);

    if (childEntites.length !== childLeaves.length) {
      throw new Error('selectSliceChildEntities: childEntites.length !== childLeaves.length. Missing entities!');
    }

    return childEntites;
  }
)

